/**
 * The Files tab's rows (design "Flow Project Redesign", Files board): everything under the project's `brain/` as one
 * list. A file dropped in (`brain/inputs/original`) is an Input, one the project made (`brain/outputs`) a Result, any
 * other file in a brain folder is Brain. A project's own `screenshots/` folder (when there is one) lists too, as
 * Screenshots: the Screenshots filter shows those and any image named like a screenshot. Sorted and filtered here, so
 * the table only draws.
 *
 * There is no "Used by" column: nothing the Files tab can read says which chat or agent turn used a file.
 */
export const FILE_KINDS = [
  { id: 'all', label: 'All' },
  { id: 'input', label: 'Inputs' },
  { id: 'result', label: 'Results' },
  { id: 'brain', label: 'Brain' },
  { id: 'screenshot', label: 'Screenshots' },
];

export const SCREENSHOTS_DIR = 'screenshots';
const IMAGE = /\.(png|jpe?g|gif|webp|bmp|avif|svg)$/i;
const TEXT = /\.(txt|md|markdown|csv|json|ya?ml|xml|html?|css|js|jsx|ts|tsx|py|sh|log|env\.example|toml|ini)$/i;
/** Up to this size a text file shows in the preview pane; a larger one only downloads. */
export const PREVIEW_TEXT_LIMIT = 256 * 1024;

export const isImage = (name) => IMAGE.test(String(name || ''));
const looksLikeScreenshot = (row) => isImage(row.name) && /screen ?shot|bildschirmfoto|schermata/i.test(row.name);

/** How the preview pane shows a row: 'image' | 'markdown' | 'text' | 'large' (text, too big) | 'none'. */
export function previewKind(row) {
  if (!row) { return 'none'; }
  if (isImage(row.name)) { return 'image'; }
  if (!TEXT.test(row.name)) { return 'none'; }
  if (Number.isFinite(row.size) && row.size > PREVIEW_TEXT_LIMIT) { return 'large'; }
  return /\.(md|markdown)$/i.test(row.name) ? 'markdown' : 'text';
}

/** Whether the agent reads the file on its own: everything under `brain/` (it reads the Brain before it works). */
export const agentReads = (row) => String(row?.path || '').startsWith('brain/');

const visible = (node) => (Array.isArray(node?.children) ? node.children : []).filter((child) => child?.name && !child.name.startsWith('.'));
const time = (node) => node?.modified || node?.mtime || null;

/**
 * The flat rows of a `brain/` tree (depth 3 or more) and, when given, of the project's `screenshots/` tree:
 * `{ path, name, dir, kind, size, modified }`.
 */
export function fileRows(brain, screenshots = null) {
  const rows = [];
  const add = (node, dir, kind) => {
    if (node.type === 'directory') { return; }
    rows.push({ path: `${dir}/${node.name}`, name: node.name, dir, kind, size: Number.isFinite(node.size) ? node.size : null, modified: time(node) });
  };
  visible(brain).forEach((folder) => {
    if (folder.type !== 'directory') { add(folder, 'brain', 'brain'); return; }
    if (folder.name === 'inputs') {
      visible(folder).filter((child) => child.type === 'directory' && child.name === 'original')
        .forEach((original) => visible(original).forEach((file) => add(file, 'brain/inputs/original', 'input')));
      return;
    }
    if (folder.name === 'outputs') { visible(folder).forEach((file) => add(file, 'brain/outputs', 'result')); return; }
    const walk = (node, dir) => visible(node).forEach((child) => (child.type === 'directory' ? walk(child, `${dir}/${child.name}`) : add(child, dir, 'brain')));
    walk(folder, `brain/${folder.name}`);
  });
  const shots = (node, dir) => visible(node).forEach((child) => (child.type === 'directory' ? shots(child, `${dir}/${child.name}`) : add(child, dir, 'screenshot')));
  if (screenshots) { shots(screenshots, SCREENSHOTS_DIR); }
  return rows;
}

/** Whether a row shows under a filter pill: its kind, and for Screenshots also an image named like a screenshot. */
export const matchesKind = (row, kind) => kind === 'all' || row.kind === kind || (kind === 'screenshot' && looksLikeScreenshot(row));

/** Where "Move to Brain" puts a file: images with the design references, anything else with the docs. */
export const brainFolderFor = (row) => (isImage(row.name) ? 'brain/design' : 'brain/docs');

/**
 * "Move to Brain" for the selected rows: `{ moves: [{ from, to, name }], skipped: [{ name, reason }] }`. A file already
 * in a Brain folder of its own stays; the others get a free name in their Brain folder (never over a file there).
 */
export function brainMoves(rows, selected, uniqueName) {
  const taken = {};
  rows.forEach((row) => { (taken[row.dir] ||= []).push(row.name); });
  const moves = [];
  const skipped = [];
  rows.filter((row) => selected.includes(row.path)).forEach((row) => {
    if (row.kind === 'brain') { skipped.push({ name: row.name, reason: 'it is already in the Brain' }); return; }
    const dir = brainFolderFor(row);
    const name = uniqueName(row.name, taken[dir] || []);
    (taken[dir] ||= []).push(name);
    moves.push({ from: row.path, to: `${dir}/${name}`, name: row.name });
  });
  return { moves, skipped };
}

export const SORTS = { name: 'name', kind: 'kind', size: 'size', modified: 'modified' };

/** Rows of one kind (`all`: every), matching the search text (name or folder), sorted by `key`, `dir` 'asc' | 'desc'. */
export function shownRows(rows, { kind = 'all', query = '', key = 'modified', dir = 'desc' } = {}) {
  const text = query.trim().toLowerCase();
  const picked = rows.filter((row) => matchesKind(row, kind) && (!text || `${row.name} ${row.dir}`.toLowerCase().includes(text)));
  const value = (row) => {
    if (key === 'size') { return row.size ?? -1; }
    if (key === 'modified') { return Date.parse(row.modified) || 0; }
    return String(row[key] || '').toLowerCase();
  };
  const sign = dir === 'asc' ? 1 : -1;
  return picked.slice().sort((a, b) => {
    const [x, y] = [value(a), value(b)];
    if (x === y) { return a.path.localeCompare(b.path); }
    return x > y ? sign : -sign;
  });
}

/** How many rows of each kind (the filter pills' counts). */
export const kindCounts = (rows) => Object.fromEntries(FILE_KINDS.map(({ id }) => [id, rows.filter((row) => matchesKind(row, id)).length]));
